import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { readSwrCache, writeSwrCache } from "../lib/swrCache";
import { externalLinkProps } from "../lib/externalLink";

// Fire-and-forget click event so the rail can personalize on next load.
// Doesn't block the navigation — if it fails, we just lose the signal.
function trackCreatorClick(v) {
  try {
    api.post("/track/creator-click", {
      channel_id: v.channel_id,
      video_id: v.video_id,
      category: v.category,
    }).catch(() => {});
  } catch (_) { /* noop */ }
}

function fmtViews(n) {
  if (!n) return "";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M views`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K views`;
  return `${n} views`;
}

function fmtAge(hours) {
  if (hours == null) return "";
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  const d = Math.floor(hours / 24);
  return d === 1 ? "1d ago" : `${d}d ago`;
}

/**
 * HomeLatestVideos — fresh uploads from our 256-creator network.
 *
 * Selection: one best video per category (max 13 cards) so the rail feels
 * varied. News is capped at 1 entry total. Shorts are pre-filtered upstream.
 *
 * UX: matches our other carousels with hover-revealed left/right scroll
 * arrows for desktop users.
 */
export default function HomeLatestVideos() {
  const [items, setItems] = useState(() => readSwrCache("creators-latest-videos"));
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  // iOS users see a subtle "Open in YouTube" pill on each card — works as a
  // reassuring signal that the link actually opens YouTube (in app if
  // installed) and lightly nudges YouTube-app installation for smoother UX.
  const [isIosUser, setIsIosUser] = useState(false);
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const ua = navigator.userAgent || "";
    setIsIosUser(/iPad|iPhone|iPod/.test(ua));
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/creators/latest-videos?limit=13&max_age_days=7");
        const arr = r.data?.items || [];
        if (alive) {
          setItems(arr);
          if (arr.length) writeSwrCache("creators-latest-videos", arr);
        }
      } catch {
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Sync arrow visibility with scroll position.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      setCanLeft(el.scrollLeft > 4);
      setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [items]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  if (!items || items.length === 0) return null;

  return (
    <section className="px-6 lg:px-10 mt-10" data-testid="home-latest-videos">
      <div className="max-w-[1440px] mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-1 h-7 bg-[#FF0000] rounded-full" />
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2">
                <Play className="w-5 h-5 text-[#FF0000] fill-[#FF0000]" />
                Latest Uploads From Creators
              </h2>
            </div>
          </div>
          <Link
            to="/creators"
            className="hidden sm:inline-flex items-center gap-1 text-xs text-white/60 hover:text-white transition group"
            data-testid="home-latest-videos-view-all"
          >
            All creators
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition" />
          </Link>
        </div>

        {/* Carousel with arrows */}
        <div className="relative group/rail">
          {canLeft && (
            <button
              onClick={() => scroll(-1)}
              aria-label="Scroll left"
              data-testid="home-latest-videos-prev"
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 -ml-2 w-10 h-10 items-center justify-center rounded-full bg-black/80 hover:bg-black border border-white/15 hover:border-white/30 shadow-2xl opacity-0 group-hover/rail:opacity-100 transition-opacity"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
          )}
          {canRight && (
            <button
              onClick={() => scroll(1)}
              aria-label="Scroll right"
              data-testid="home-latest-videos-next"
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 -mr-2 w-10 h-10 items-center justify-center rounded-full bg-black/80 hover:bg-black border border-white/15 hover:border-white/30 shadow-2xl opacity-0 group-hover/rail:opacity-100 transition-opacity"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          )}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-3 -mx-2 px-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-white/10"
            data-testid="home-latest-videos-list"
          >
            {items.map((v, idx) => (
              <a
                key={v.video_id}
                {...externalLinkProps(v.watch_url)}
                onClick={() => trackCreatorClick(v)}
                data-testid={`latest-video-card-${idx}`}
                className="group shrink-0 w-[280px] sm:w-[320px] snap-start rounded-xl overflow-hidden bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 hover:border-[#FF0000]/40 transition-all"
              >
                <div className="relative aspect-video bg-black overflow-hidden">
                  <img
                    src={v.thumb}
                    alt={v.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                    <div className="w-14 h-14 rounded-full bg-[#FF0000] flex items-center justify-center shadow-2xl shadow-[#FF0000]/40">
                      <Play className="w-6 h-6 text-white fill-white ml-0.5" />
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-md bg-black/70 text-white border border-white/10 backdrop-blur-sm">
                    {fmtAge(v.hours_ago)}
                  </div>
                  {/* iOS reassurance: small "Open in YouTube" hint pill so users
                      know the tap will deep-link into the YouTube app (if
                      installed) instead of fighting Safari. Subtle — only shows
                      bottom-left, no border, faded by default, brightens on
                      hover. Web/Android users don't see this. */}
                  {isIosUser && (
                    <div
                      className="absolute bottom-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-[#FF0000]/85 text-white shadow-md backdrop-blur-sm flex items-center gap-1"
                      data-testid={`ios-youtube-hint-${idx}`}
                    >
                      <Play className="w-2.5 h-2.5 fill-white" />
                      Open in YouTube
                    </div>
                  )}
                </div>
                <div className="p-3 flex gap-2.5">
                  <img
                    src={v.channel_thumb}
                    alt={v.channel_name}
                    loading="lazy"
                    className="w-9 h-9 rounded-full ring-1 ring-white/15 object-cover shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold leading-snug line-clamp-2" title={v.title}>
                      {v.title}
                    </div>
                    <div className="text-[11px] text-white/55 mt-1 truncate">
                      {v.channel_name}
                    </div>
                    <div className="text-[10px] text-white/40 mt-0.5">
                      {fmtViews(v.view_count)}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
